import type { ComputerSetupBridge } from "./computerSetup.ts";
import * as Schema from "effect/Schema";

export const ConnectInstallation = Schema.Struct({
  installationId: Schema.String,
  credential: Schema.String,
});
/**
 * What AFAR Connect's `/v1/connect` hands back once a workspace's Environment has minted a grant
 * for this client. The endpoint is that Environment's own hostname: the client redeems the
 * credential there directly, and AFAR Connect carries nothing further.
 */
export const ConnectBrokeredConnection = Schema.Struct({
  environmentId: Schema.String,
  label: Schema.String,
  endpoint: Schema.String,
  credential: Schema.String,
  expiresAt: Schema.Number,
});
export type ConnectBrokeredConnection = typeof ConnectBrokeredConnection.Type;

export const ConnectAccount = Schema.Struct({
  id: Schema.String,
  name: Schema.optionalKey(Schema.String),
  email: Schema.optionalKey(Schema.String),
  issuer: Schema.optionalKey(Schema.String),
});
export type ConnectAccount = typeof ConnectAccount.Type;

export const ConnectAccountStatus = Schema.Struct({
  accounts: Schema.optionalKey(Schema.Array(ConnectAccount)),
  activeAccountId: Schema.optionalKey(Schema.String),
  /**
   * `needs-sign-in` is a saved account whose credential no longer authenticates: the record is
   * still here, but nothing it holds will be accepted until the user signs in again. It is
   * distinct from `signed-out`, which has no record at all, and it exists because reporting a
   * dead credential as signed in is what let the app tell a signed-in user to sign in (#1008).
   */
  state: Schema.Literals(["unavailable", "signed-out", "signed-in", "needs-sign-in"]),
  message: Schema.optionalKey(Schema.String),
});
export type ConnectAccountStatus = typeof ConnectAccountStatus.Type;

/**
 * Where a Cloud Workspace answers, and what this client may say to it. The endpoint is the
 * workspace's own hostname; AFAR Connect is not on the path it describes.
 */
export const CloudWorkspaceEndpoint = Schema.Struct({
  httpBaseUrl: Schema.String,
  wsBaseUrl: Schema.String,
  /** Absent when the destination was reached but issued no Environment Access. */
  session: Schema.optionalKey(Schema.String),
  /** Names the session on the destination, so this device can be shown and revoked there. */
  sessionId: Schema.optionalKey(Schema.String),
  /** When the session stops being accepted, as the destination reported it. */
  sessionExpiresAt: Schema.optionalKey(Schema.Int),
});
export type CloudWorkspaceEndpoint = typeof CloudWorkspaceEndpoint.Type;

/**
 * What a Cloud Workspace introduction came to. A refusal travels as data rather than a thrown
 * error because Desktop IPC keeps only an error's message, and the client's next step (retry,
 * start the workspace, sign in) depends on Connect's code and status.
 */
export const CloudWorkspaceConnectOutcome = Schema.Union([
  Schema.TaggedStruct("CloudWorkspaceOpened", { endpoint: CloudWorkspaceEndpoint }),
  Schema.TaggedStruct("CloudWorkspaceRefused", { code: Schema.String, status: Schema.Int }),
]);
export type CloudWorkspaceConnectOutcome = typeof CloudWorkspaceConnectOutcome.Type;

export interface ConnectDesktopBridge {
  contributionProject?(
    projectId: string,
  ): Promise<import("./projectContributionSnapshot.ts").PublicContributionProjectPage | null>;
  contributionProjects?(): Promise<
    readonly import("./projectContributionSnapshot.ts").PublicContributionProject[]
  >;
  createContributionProject?(
    input: import("./projectContributionSnapshot.ts").CreatePublicContributionProjectInput,
  ): Promise<import("./projectContributionSnapshot.ts").PublicContributionProject>;
  publishProjectContribution?(
    projectId: string,
    attemptId: string,
    snapshot: import("./projectContributionSnapshot.ts").ProjectContributionSnapshot,
  ): Promise<import("./projectContributionSnapshot.ts").PublishedProjectContribution>;
  withdrawProjectContribution?(
    projectId: string,
    snapshotId?: string,
  ): Promise<{ withdrawn: boolean }>;
  computers?: ComputerSetupBridge;
  /** AFAR Networks and Organizations, offered by the Runtime Web Client only (ADR-0146). */
  networks?: import("./afarNetwork.ts").AfarNetworkBridge;
  /**
   * Reaches one Cloud Workspace's Environment over its Cloud Workspace Connection (ADR-0122): AFAR Connect brokers the
   * introduction, and this device redeems the result directly with the destination. Every surface
   * holding an account can do this, which is why it is not Desktop-only.
   */
  connectEnvironment(request: {
    environmentId: string;
    label: string;
  }): Promise<CloudWorkspaceConnectOutcome>;
  /**
   * Cloud workspaces and Usage Allowances managed through Connect (ADR-0129).
   * Optional because a client may not have Connect configured.
   */
  cloudAllowance?(): Promise<import("./cloudWorkspace.ts").CloudAllowanceStatus>;
  cloudAccounts?(
    search: string,
  ): Promise<readonly import("./cloudWorkspace.ts").CloudAccountAllowance[]>;
  cloudAllowanceHistory?(
    accountId: string,
  ): Promise<readonly import("./cloudWorkspace.ts").CloudAllowanceHistory[]>;
  adjustCloudAllowance?(
    request: import("./cloudWorkspace.ts").AdjustCloudAllowance,
  ): Promise<{ recorded: boolean }>;
  changeCloudSubscription?(
    request: import("./cloudWorkspace.ts").ChangeCloudSubscription,
  ): Promise<{ recorded: boolean }>;
  cloudWorkspaces?(): Promise<readonly import("./cloudWorkspace.ts").CloudWorkspace[]>;
  createCloudWorkspace?(
    request: import("./cloudWorkspace.ts").CreateCloudWorkspace,
  ): Promise<import("./cloudWorkspace.ts").CloudWorkspace>;
  cloudWorkspaceAction?(request: import("./cloudWorkspace.ts").CloudWorkspaceAction): Promise<void>;
  /**
   * Stops this device from reaching a Linked Computer: its Environment Access goes. Local — the
   * computer keeps running, and a session it issued to another device is that device's to give up.
   */
  forgetComputer?(environmentId: string): Promise<void>;
  getStatus(): Promise<ConnectAccountStatus>;
  startProviderBrowserLogin?(
    environmentId: string,
    instanceId: string,
  ): Promise<import("./server.ts").AppOwnedCodexAuthenticationResult>;
  cancelProviderBrowserLogin?(environmentId: string, instanceId: string): Promise<void>;
  switchAccount?(id: string): Promise<void>;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  installations(): Promise<readonly { installationId: string }[]>;
  revokeInstallation(installationId: string): Promise<void>;
}

export const ConnectProviderAuthenticationRequest = Schema.Struct({
  operation: Schema.Literals(["start", "status", "callback", "cancel"]),
  instanceId: Schema.String,
  attemptId: Schema.String,
  callback: Schema.optionalKey(Schema.String),
});
export type ConnectProviderAuthenticationRequest = typeof ConnectProviderAuthenticationRequest.Type;
export const ConnectProviderAuthenticationResponse = Schema.Struct({
  instanceId: Schema.optionalKey(Schema.String),
  method: Schema.optionalKey(Schema.String),
  message: Schema.optionalKey(Schema.String),
  outcome: Schema.optionalKey(Schema.String),
  authUrl: Schema.optionalKey(Schema.String),
  status: Schema.optionalKey(Schema.Number),
  body: Schema.optionalKey(Schema.String),
  contentType: Schema.optionalKey(Schema.String),
  location: Schema.optionalKey(Schema.String),
});
export type ConnectProviderAuthenticationResponse =
  typeof ConnectProviderAuthenticationResponse.Type;
